/**
 * SEO Configuration for AcquireFlow
 * Centralized SEO metadata for consistent optimization
 */

export const seoConfig = {
  // Site Information
  siteName: 'AcquireFlow',
  siteUrl: 'https://acquireflow.com', // Update with actual domain
  defaultTitle: 'AcquireFlow - Automate Your MLS Offers with AI',
  titleTemplate: '%s | AcquireFlow',
  defaultDescription:
    'Join AcquireFlow beta for $199 and lock in lifetime 50% discount. Send 1,000+ automated MLS offers daily with AI-powered targeting. 45-day trial included.',

  // Keywords
  keywords: [
    'real estate automation',
    'MLS offers',
    'real estate investing',
    'property investment software',
    'automated offers',
    'real estate AI',
    'deal flow automation',
    'property acquisition',
    'real estate wholesaling',
    'investment property software',
    'real estate beta',
    'early access real estate software',
  ],

  // Social Media
  social: {
    twitter: '@acquireflow', // Update with actual handle
    facebook: 'acquireflow', // Update with actual page
    linkedin: 'company/acquireflow', // Update with actual company
  },

  // Open Graph
  ogImage: 'https://acquireflow.com/og-image.jpg', // Update with actual image
  ogImageWidth: 1200,
  ogImageHeight: 630,

  // Twitter Card
  twitterCard: 'summary_large_image',
  twitterImage: 'https://acquireflow.com/twitter-card.jpg', // Update with actual image

  // Organization Schema
  organization: {
    name: 'AcquireFlow',
    url: 'https://acquireflow.com',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mWrZcYtRDkuuJnVj5p5E1s/AcquireFlow_Logo_%288%29.png',
    description:
      'AI-powered real estate investment automation platform helping investors send automated MLS offers and triple their deal volume.',
    foundingDate: '2024',
    contactPoint: {
      contactType: 'Customer Support',
      email: 'support@acquireflow.com', // Update with actual email
    },
  },

  // Product Schema
  product: {
    name: 'AcquireFlow Beta',
    description:
      'Join AcquireFlow beta for exclusive early access. $199 one-time fee for lifetime 50% discount on all plans. Automated MLS offer platform for real estate investors.',
    category: 'Real Estate Software',
    offers: {
      beta: {
        name: 'Beta Access',
        price: '199',
        priceCurrency: 'USD',
        priceValidUntil: '2025-12-31',
        description: 'One-time fee for lifetime 50% discount on all plans',
      },
      core: {
        name: 'Core Plan (Beta Pricing)',
        price: '499',
        priceCurrency: 'USD',
        priceValidUntil: '2025-12-31',
        description: 'Lifetime 50% discount for beta users',
      },
      scale: {
        name: 'Scale Plan (Beta Pricing)',
        price: '799',
        priceCurrency: 'USD',
        priceValidUntil: '2025-12-31',
        description: 'Lifetime 50% discount for beta users',
      },
    },
  },

  // FAQ Schema Data
  faqSchema: [
    {
      question: 'How does AcquireFlow work?',
      answer:
        'AcquireFlow automates your entire MLS offer process using AI. It searches MLS listings, filters for motivated sellers, and sends personalized offers automatically.',
    },
    {
      question: 'How many offers can I send per day?',
      answer:
        'Core plan allows 3,000 automated offers per month. Scale plan offers unlimited automated offers.',
    },
    {
      question: 'What is the beta program?',
      answer:
        'Join AcquireFlow beta for a $199 one-time fee and receive lifetime 50% discount on all plans, 45-day trial period, priority early access, and a free 1-on-1 setup call.',
    },
    {
      question: 'What happens after the 45-day trial?',
      answer:
        'After your 45-day trial, you can choose to continue with either the Core or Scale plan at your locked-in beta pricing (50% off for life).',
    },
    {
      question: 'What markets does AcquireFlow support?',
      answer:
        'Core plan supports 1 target market. Scale plan supports unlimited markets across the United States.',
    },
  ],
} as const

export type SEOConfig = typeof seoConfig
